import type { Cents } from "./money";
import type { CostType } from "./proposedCostLine";

/**
 * BASELINES & CHANGE ORDERS (Phase 11, spec §27).
 *
 * THE PERMANENT PRINCIPLE: **working estimates may change; baselines do
 * not.** A Baseline is the frozen expectation the owner committed to; every
 * later change is explained relative to it rather than quietly rewriting it.
 *
 * Four numbers, never conflated:
 *
 *   BASELINE                — what was approved, frozen forever
 *   AUTHORIZED BUDGET       — baseline + approved Change Orders
 *   CURRENT WORKING ESTIMATE— the live estimate, free to move
 *   ACTUAL                  — real money (Phase 9C), never touched by either
 *
 * And two design roles, never conflated (spec §17 / §27):
 *
 *   ACTIVE DESIGN   — the plan currently being worked from
 *   APPROVED DESIGN — the plan the baseline was approved against
 *
 * Working in another Proposed design changes the Active design only. The
 * Approved design moves ONLY through an explicit approval.
 */

export type BaselineStatus =
  /** The live approved baseline. */
  | "approved"
  /** Replaced by an administrative correction; kept as history. */
  | "superseded";

export interface Baseline {
  id: string;
  projectId: string;
  /** 1 for the Initial Baseline; higher only after an explicit correction. */
  sequence: number;
  name: string;
  status: BaselineStatus;
  /** The design this baseline approved (null for design-less projects). */
  designId: string | null;
  designName: string;
  /** Frozen estimating assumptions — later changes never move these. */
  contingencyCents: Cents;
  estimatingTaxPctX100: number | null;
  laborRateCentsPerHour: number;
  /** Frozen acquisition assumptions, for later outcome comparison (§28). */
  purchasePriceCents: Cents | null;
  expectedArvCents: Cents | null;
  /** Σ of the frozen scope lines. */
  scopeCents: Cents;
  /** scopeCents + contingencyCents — the authorized budget at approval. */
  totalCents: Cents;
  notes: string;
  /** Actor-ready for SaaS; null on the desktop build. */
  approvedBy: string | null;
  approvedAt: string;
  supersededAt: string | null;
  supersededReason: string;
  createdAt: string;
  lines: BaselineLine[];
}

/**
 * One frozen estimate line. Enough to answer, forever: what scope existed,
 * what it cost, what quantity drove it, which product/price assumption was
 * used, and where it belonged.
 */
export interface BaselineLine {
  id: string;
  baselineId: string;
  /** Lineage back to the live line (may since have been archived). */
  sourceLineId: string | null;
  featureId: string | null;
  featureName: string;
  roomId: string | null;
  roomName: string;
  designId: string | null;
  categoryId: string | null;
  costType: CostType | null;
  description: string;
  amountCents: Cents;
  /** The driving quantity behind the line, when it had one. */
  quantityX100: number | null;
  quantityUnit: string | null;
  unitCostCents: Cents | null;
  /** Which product assumption priced it (a snapshot, never a live offer). */
  productId: string | null;
  productName: string;
  /** Assembly provenance. */
  assemblyComponentId: string | null;
  assemblyName: string;
  /** Package provenance carried through (Phase 10 → future analytics). */
  packageApplicationId: string | null;
  packageName: string;
  sortOrder: number;
}

export type ChangeOrderStatus = "draft" | "approved" | "cancelled";

export const CHANGE_ORDER_STATUSES: ReadonlyArray<{ id: ChangeOrderStatus; displayName: string }> = [
  { id: "draft", displayName: "Draft" },
  { id: "approved", displayName: "Approved" },
  { id: "cancelled", displayName: "Cancelled" },
];

export interface ChangeOrder {
  id: string;
  projectId: string;
  baselineId: string;
  /** Human-readable, stable, never reused: CO-001. Never database identity. */
  number: string;
  title: string;
  status: ChangeOrderStatus;
  /** The design this change was authorized against, when relevant. */
  designId: string | null;
  designName: string;
  /** Σ of the line deltas — may legitimately be negative. */
  netDeltaCents: Cents;
  /**
   * How the change is funded (spec §27): 'budget' raises the authorized
   * budget; 'contingency' draws on the frozen contingency, which is already
   * inside the baseline, so the authorized total does not move.
   */
  fundingSource: "budget" | "contingency";
  /** The contingency-funded portion (a split records less than the delta). */
  contingencyCents: Cents;
  reason: string;
  notes: string;
  createdBy: string | null;
  approvedBy: string | null;
  createdAt: string;
  approvedAt: string | null;
  cancelledAt: string | null;
  lines: ChangeOrderLine[];
}

export type ChangeKind = "added" | "removed" | "changed" | "manual";

export interface ChangeOrderLine {
  id: string;
  changeOrderId: string;
  kind: ChangeKind;
  description: string;
  featureId: string | null;
  featureName: string;
  roomName: string;
  categoryId: string | null;
  costType: CostType | null;
  /** What the baseline (or previously authorized state) said. */
  baselineAmountCents: Cents;
  /** What the current plan says. */
  currentAmountCents: Cents;
  /** currentAmount − baselineAmount; negative reduces the budget. */
  deltaCents: Cents;
  quantityDeltaX100: number | null;
  quantityUnit: string | null;
  productId: string | null;
  productName: string;
  /** Lineage so the same change is never authorized twice. */
  baselineLineId: string | null;
  sourceLineId: string | null;
  notes: string;
  sortOrder: number;
}

export function validateBaselineInput(input: { name: string }): string[] {
  const problems: string[] = [];
  if (input.name.trim() === "") problems.push("A baseline needs a name.");
  return problems;
}

export function validateChangeOrderInput(input: { title: string }): string[] {
  const problems: string[] = [];
  if (input.title.trim() === "") problems.push("A change order needs a title.");
  return problems;
}

/** CO-001 … CO-999+ — stable, never reused, never database identity. */
export function formatChangeOrderNumber(sequence: number): string {
  return `CO-${String(sequence).padStart(3, "0")}`;
}
